import { useCallback, useMemo } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { UserRole } from '@cargovibe/shared-types';

interface User {
  id: string;
  name: string;
  email: string;
  /** null, wenn die Rolle aus den Clerk-Metadaten nicht eindeutig geparsed werden kann. */
  role: UserRole | null;
  avatarUrl?: string;
}

export interface AuthState {
  user: User | null;
  logout: () => Promise<void>;
}

function getRoleFromMetadata(metadata: Record<string, unknown> | undefined): UserRole | null {
  const raw: unknown = metadata?.role;
  return Object.values(UserRole).find((role: UserRole): boolean => role === raw) ?? null;
}

export function useAuth(): AuthState {
  const { user: clerkUser } = useUser();
  const { signOut } = useClerk();

  const logout: () => Promise<void> = useCallback(async (): Promise<void> => {
    await signOut({ redirectUrl: '/' });
  }, [signOut]);

  const user: User | null = useMemo<User | null>(() => {
    if (!clerkUser) return null;

    const name: string =
      clerkUser.fullName ||
      [clerkUser.firstName, clerkUser.lastName].filter(Boolean).join(' ').trim() ||
      clerkUser.primaryEmailAddress?.emailAddress ||
      'User';

    return {
      id: clerkUser.id,
      name,
      email: clerkUser.primaryEmailAddress?.emailAddress ?? '',
      role: getRoleFromMetadata(clerkUser.publicMetadata),
      avatarUrl: clerkUser.imageUrl,
    };
  }, [clerkUser]);

  return {
    user,
    logout,
  };
}
